import { ILesson } from '@dtos/Course'
import { IEnrollment, ILessonsProgresses } from '@dtos/Enrollment'
import {
  IRefetchEnrollment,
  useEnrollment,
} from '@services/api/graphql/enrollment'
import { NetworkStatus, useApolloClient } from '@apollo/client'
import { useGetLastLessonsViewedContent } from '@services/api/graphql/lastLessonsViewedContent'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useFocusEffect } from 'expo-router'
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useApolloErrorToast } from '@hooks/useApolloErrorToast'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

export interface IEnrollmentData extends IEnrollment {
  lastLessonsViewedContent: { nodes: ILesson[] }
}

interface IEnrollmentProviderProps {
  enrollment?: IEnrollmentData | null
  enrollmentIsLoading: boolean
  error?: Error
  onGetLastLessonsViewed?: () => void
  updateEnrollmentState: (enrollment: IEnrollment) => void
  /**
   * Rebusca a matrícula e o gate do progresso linear. Usado quando o servidor confirma a
   * conclusão de uma aula: o gate é calculado no servidor e a atualização otimista local
   * só sabe mexer em `lessonsProgresses`.
   */
  refetchEnrollment: () => void
}

export const EnrollmentContext = createContext<IEnrollmentProviderProps>(
  {} as IEnrollmentProviderProps,
)

interface IUseEnrollmentInterface {
  data: {
    enrollment: IEnrollment
  } | null
  loading: boolean
  error: Error | undefined
  refetch: IRefetchEnrollment
  networkStatus: NetworkStatus
  // `updateQuery` opcional: a mescla das páginas de lessonsProgresses mora no
  // field policy do cache (offsetMergeFieldPolicy), não aqui.
  fetchMore: (options: {
    variables: { pagination: { offset: number; limit: number } }
    updateQuery?: (
      previousResult: { enrollment: IEnrollment } | null,
      options: {
        fetchMoreResult: { enrollment: IEnrollment } | null
      },
    ) => { enrollment: IEnrollment } | null
  }) => Promise<unknown>
}

const ENROLLMENT_ORDENATION = {
  by: 'last_view_at',
  direction: 'desc',
} as const

// Âncora fixa no passado para o preview: qualquer releaseAfter/lockAfter
// medido a partir dela já venceu.
const PREVIEW_ACTIVATED_AT = '2000-01-01T00:00:00.000Z'

const ENROLLMENT_PAGE_SIZE = 100
const ENROLLMENT_PAGINATION = {
  offset: 0,
  limit: ENROLLMENT_PAGE_SIZE,
} as const

export function EnrollmentProvider({ children }: { children: ReactNode }) {
  const showApolloErrorToast = useApolloErrorToast()
  const apolloClient = useApolloClient()
  const { enrollmentId, courseId } = useCourseRouteParams()
  const { isPreview } = useAdminPreview()

  // Optimistic state for immediate UI updates (e.g., lesson completion in MobileLayout)
  const [optimisticEnrollment, setOptimisticEnrollment] =
    useState<IEnrollment | null>(null)

  const { data, loading, error, refetch, networkStatus, fetchMore } =
    useEnrollment(
      `${enrollmentId}`,
      ENROLLMENT_ORDENATION,
      `${courseId}`,
      ENROLLMENT_PAGINATION,
      // No preview do produtor não existe matrícula: a query responderia
      // unauthorized (EnrollmentType autoriza por school+student).
      { skip: isPreview },
    ) as IUseEnrollmentInterface

  const {
    getLastLessonsViewed,
    data: lastLessonsViewedContent,
    loading: lastLessonsViewedLoading,
    called,
  } = useGetLastLessonsViewedContent({
    id: `${courseId}`,
    offset: 0,
    limit: 5,
    lessonsIds: [],
  })

  // Matrícula sintética do preview. É o mesmo recurso do legado, onde o core
  // injeta um Enrollment.new para renderizar as telas do aluno — e, como lá, o
  // objeto precisa estar COMPLETO: as telas leem enrollment.schoolProduct,
  // .activatedAt, .courseClassRelease etc. sem optional chaining, então um stub
  // parcial derruba o render (foi o que aconteceu com schoolProduct em
  // CourseDetails). Ao adicionar campo ao IEnrollment, adicione aqui também.
  const previewEnrollment = useMemo(
    () =>
      ({
        id: null,
        status: 'active',
        // Data no passado de propósito: offsets de releaseAfter/lockAfter já
        // venceram, então nada aparece bloqueado por data e o produtor vê o
        // curso inteiro — mesmo comportamento do legado.
        activatedAt: PREVIEW_ACTIVATED_AT,
        availableUntil: null,
        // getAccessDays devolve null com unlimited, então o preview não mostra
        // badge de "expira em N dias" para um acesso que não existe.
        unlimited: true,
        progressCard: {
          id: 0,
          progress: 0,
          total: 0,
          completed: 0,
          courseId: null,
          lessonsProgresses: [],
          courseProgressCards: null,
        },
        livemeetRoomsAvailable: [],
        // certificationMinProgress alto de propósito: com progress 0, um mínimo
        // 0 faria `0 >= 0` e liberaria o botão de certificado no preview.
        schoolProduct: {
          id: 0,
          title: '',
          description: '',
          logo: '',
          logoVertical: '',
          certification: false,
          certificationMinProgress: 100,
          niche: null,
          path: null,
          expireDate: null,
          availableTimeLength: 0,
          courses: null,
        },
        courseClassRelease: null,
        certificates: [],
        lessonsProgresses: [],
        lessonsCompletedPerModule: {},
      }) as unknown as IEnrollment,
    [],
  )

  // Derive enrollment: prefer optimistic update, fallback to Apollo data
  const enrollment = isPreview
    ? previewEnrollment
    : (optimisticEnrollment ?? data?.enrollment ?? null)

  // Only show loading on initial fetch, not on background refetch (networkStatus 4)
  const isRefetching = networkStatus === NetworkStatus.refetch
  const enrollmentIsLoading = isPreview
    ? false
    : (loading && !isRefetching) || lastLessonsViewedLoading

  const updateEnrollmentState = useCallback((newEnrollment: IEnrollment) => {
    setOptimisticEnrollment(newEnrollment)
  }, [])

  // No preview não existe query de matrícula para rebuscar (skip).
  const refetchEnrollment = useCallback(() => {
    if (isPreview) return

    refetch()
    // O gate do progresso linear vive em query própria (ver getLinearProgressGate), então
    // não vem junto no refetch acima.
    apolloClient.refetchQueries({ include: ['getLinearProgressGate'] })
  }, [isPreview, refetch, apolloClient])

  // Clear optimistic state when fresh data arrives from server
  const prevDataEnrollmentRef = useRef(data?.enrollment)
  useEffect(() => {
    if (
      data?.enrollment &&
      data?.enrollment !== prevDataEnrollmentRef.current &&
      optimisticEnrollment
    ) {
      setOptimisticEnrollment(null)
    }
    prevDataEnrollmentRef.current = data?.enrollment
  }, [data?.enrollment, optimisticEnrollment])

  // Incrementally fetch remaining lessonsProgresses pages so large courses
  // don't request all rows in a single payload (avoids GraphQL network errors
  // and OOM on low-memory devices). See Sentry MEMBERS-EXPO-8Y.
  const isFetchingMoreRef = useRef(false)
  const loadedProgressesCount =
    data?.enrollment?.progressCard?.lessonsProgresses?.length ?? 0
  // Offsets já pedidos nesta matrícula. A condição antiga comparava
  // `lessonsProgresses.length` com `progressCard.total`, que são grandezas
  // diferentes: `total` é o número de conteúdos do curso, não de progressos
  // existentes. Num curso de 45 aulas com 1 progresso, 1 < 45 sempre — o
  // effect pedia offset=1 e, como o count não crescia, repetia a mesma página.
  // Agora só paginamos enquanto a última página vier cheia, e nunca repetimos
  // um offset.
  const enrollmentKey = `${enrollmentId}:${courseId}`
  const pagerRef = useRef({
    key: enrollmentKey,
    count: loadedProgressesCount,
    offsets: new Set<number>(),
  })
  // A sessão de paginação é invalidada por dois sinais. Os dois moram no MESMO
  // ref porque decidem a mesma coisa; antes eram três refs mutados em pontos
  // diferentes do corpo do render, com duas convenções de atualização.
  //
  // 1. Troca de matrícula/curso: a lista é outra, os offsets não valem mais.
  // 2. A lista ENCOLHEU. Quem impede isso agora é o cache: o field policy de
  //    `ProgressCard.lessonsProgresses` (offsetMergeFieldPolicy) mescla as
  //    páginas por offset, então o refetch com as variáveis ORIGINAIS (offset 0)
  //    reescreve só as primeiras posições em vez de substituir a lista. Este
  //    ramo fica como rede para o que o field policy não cobre — despejo/gc do
  //    cache — porque sem ele a lista ficaria presa na PRIMEIRA página e as
  //    aulas de fora dela voltariam a aparecer como não concluídas.
  //
  // A guarda anti-loop continua: fetchMore que não cresce o count (ex.: página
  // vazia no fim da lista) mantém o offset no Set e não é pedido de novo.
  if (
    pagerRef.current.key !== enrollmentKey ||
    loadedProgressesCount < pagerRef.current.count
  ) {
    pagerRef.current = {
      key: enrollmentKey,
      count: loadedProgressesCount,
      offsets: new Set<number>(),
    }
  } else {
    pagerRef.current.count = loadedProgressesCount
  }
  useEffect(() => {
    if (isFetchingMoreRef.current) return
    if (loadedProgressesCount === 0) return
    if (loadedProgressesCount % ENROLLMENT_PAGE_SIZE !== 0) return
    const requestedOffsets = pagerRef.current.offsets
    if (requestedOffsets.has(loadedProgressesCount)) return

    requestedOffsets.add(loadedProgressesCount)
    isFetchingMoreRef.current = true
    fetchMore({
      variables: {
        pagination: {
          offset: loadedProgressesCount,
          limit: ENROLLMENT_PAGE_SIZE,
        },
      },
      // SEM updateQuery de propósito: quem mescla é o field policy do cache. A
      // concatenação manual (`[...previous, ...new]`) colava a página no fim da
      // lista que existisse NAQUELE momento — se um refetch tivesse cortado a
      // lista no meio do voo, a página 3 caía em cima da página 1 e ficava um
      // buraco permanente (comprimento fora do múltiplo da página = paginação
      // para de vez). O merge por offset escreve na posição certa ou descarta.
    })
      .catch(() => {
        // Libera o offset para nova tentativa — mesma regra do
        // @hooks/useGuardedLoadMore, que documenta "offset é liberado em caso de
        // erro para permitir nova tentativa". NÃO repede aqui: quem repede é a
        // próxima renderização, senão um erro persistente vira laço de requests.
        requestedOffsets.delete(loadedProgressesCount)
      })
      .finally(() => {
        isFetchingMoreRef.current = false
      })
  }, [loadedProgressesCount, fetchMore])

  // Fetch last viewed lessons when progresses data changes
  const lessonsProgresses = data?.enrollment?.progressCard?.lessonsProgresses
  useEffect(() => {
    if (lessonsProgresses && lessonsProgresses.length > 0) {
      getLastLessonsViewed({
        variables: {
          offset: 0,
          limit: 5,
          lessonsIds: lessonsProgresses
            .map((lesson: ILessonsProgresses) => Number(lesson.lessonId))
            .slice(0, 5),
        },
      })
    }
  }, [lessonsProgresses, getLastLessonsViewed])

  // Refetch enrollment on screen focus (only after initial data is loaded).
  //
  // A condição "só depois do primeiro dado" mora num REF, não nas dependências:
  // `useFocusEffect` do react-navigation é um `useEffect(..., [effect])` que
  // reexecuta o callback sempre que a tela está focada e a identidade dele muda.
  // Com `data?.enrollment` nas dependências, TODA resposta nova disparava um
  // refetch — e junto com a repaginação pós-refetch (acima) isso fecha um ciclo
  // que não converge: refetch corta a lista -> o Set de offsets é liberado ->
  // fetchMore remonta a lista -> nova identidade de `data.enrollment` -> refetch.
  // Medido numa matrícula de 133 progressos com a tela parada: 41 refetches e 21
  // fetchMores em 320 ms; com o ref, 1 e 2, e a lista fica mesclada em 133.
  const hasEnrollmentDataRef = useRef(false)
  hasEnrollmentDataRef.current = !!data?.enrollment
  useFocusEffect(
    useCallback(() => {
      if (!hasEnrollmentDataRef.current) return
      refetch()
    }, [refetch]),
  )

  useEffect(() => {
    if (error) {
      showApolloErrorToast(error, 'apolloErrors.enrollment.title')
    }
  }, [error, showApolloErrorToast])

  const contextValue = useMemo(
    () => ({
      enrollment: enrollment
        ? {
            ...enrollment,
            lastLessonsViewedContent: {
              ...lastLessonsViewedContent?.course.lessons,
            },
          }
        : null,
      enrollmentIsLoading: enrollmentIsLoading && !called,
      error,
      updateEnrollmentState,
      refetchEnrollment,
    }),
    [
      enrollment,
      lastLessonsViewedContent,
      enrollmentIsLoading,
      called,
      error,
      updateEnrollmentState,
      refetchEnrollment,
    ],
  )

  return (
    <EnrollmentContext.Provider value={contextValue}>
      {children}
    </EnrollmentContext.Provider>
  )
}
